import { CareTeamMembersSchema, TeamMembersValidationSchema } from '@quromedical/models';
import { CrudForm } from 'components/form';
import { SelectOption } from 'components/types';
import { OrganizationFetcher } from 'core/group';
import React, { useCallback } from 'react';
import { strings } from 'strings';

import { teamFields } from './fields';

interface GeneralFormProps {
  organizationFetcher: OrganizationFetcher;
  showErrors?: boolean;
  data: Partial<CareTeamMembersSchema>;
  onChange: (data: Partial<CareTeamMembersSchema>, isValid: boolean) => void;
  onRemove?: () => void;
  onAdd?: () => void;
}

interface FormCareTeamLine extends CareTeamMembersSchema {
  teamMembers: SelectOption<never, string>;
}

export const GeneralForm: React.FC<GeneralFormProps> = ({
  data,
  showErrors = false,
  onChange,
  onRemove,
  onAdd,
}) => {
  const handleChange = useCallback(
    (data: Partial<CareTeamMembersSchema>) => {
      const newState: Partial<CareTeamMembersSchema> = {
        role: data.role,
        member: data.member,
      };

      // revalidating since the derived validation may not be up to date
      const isValid = TeamMembersValidationSchema.safeParse(data);

      onChange(newState, isValid.success);
    },
    [onChange]
  );

  const selectedItem: SelectOption<never, string> | undefined = data.role
    ? {
        value: data.role,
        display: data.role || '',
      }
    : undefined;

  const fieldData = teamFields();

  const formValue: Partial<FormCareTeamLine> = {
    ...data,
    teamMembers: selectedItem,
  };

  const onSubmit = useCallback(async () => {
    try {
      onAdd?.();
      return {};
    } catch (error) {
      return { error };
    }
  }, [onAdd]);

  return (
    <CrudForm<FormCareTeamLine>
      key={formValue.teamMembers?.value}
      hasCard={false}
      onChange={handleChange}
      onChangeDebounceTime={0}
      buttonVariant="outlined"
      fields={fieldData}
      showErrors={showErrors}
      onSubmit={onSubmit}
      showSubmitButton={!!onAdd}
      buttonText={strings.FormListAddItem}
      onSecondarySubmit={onRemove}
      showSecondarySubmitButton={!!onRemove}
      secondaryButtonText={strings.FormListRemoveItem}
      initialValues={formValue}
      validationSchema={TeamMembersValidationSchema}
    />
  );
};
